<template>
  <v-container fluid class="sc">
    <v-snackbar v-model="snackbar" bottom :timeout="timeout" :color="color">{{ text }}</v-snackbar>

    <div class="sc-head">
      <div class="sc-firma">
        <div class="sc-firma-ime">{{ comp.CompanyNameShort }}</div>
        <div>{{ comp.CompanyStreet }} {{ comp.CompanyBuildingNameNumber }}</div>
        <div>{{ comp.CompanyPostcode }} {{ comp.CompanyCity }}</div>
        <div>OIB: {{ comp.CompanyVATID }}</div>
      </div>
      <div class="sc-naslov">
        <h2>Sidrene cijene</h2>
        <div class="sc-meta">
          dodatna cijena i objava cjenika<span class="sc-sep">·</span>NN 101/2026<span class="sc-sep">·</span>primjena od 1.10.2026.
        </div>
      </div>
      <div class="sc-akcije">
        <v-btn small outlined :loading="ucitavam" @click="ucitaj">Osvježi</v-btn>
      </div>
    </div>

    <div class="sc-sazetak">
      <div class="sc-kut">
        <span>Artikala</span>
        <b>{{ artikli.length }}</b>
        <small>u prodaji</small>
      </div>
      <div class="sc-kut">
        <span>Proizvodi</span>
        <b>{{ brojProizvoda }}</b>
        <small>{{ brojUsluga }} usluga</small>
      </div>
      <div class="sc-kut sc-istaknuto">
        <span>Sa sidrenom cijenom</span>
        <b>{{ brojSaSidrenom }}</b>
        <small>{{ postotakSidrenih }} % artikala</small>
      </div>
      <div class="sc-kut" :class="{'sc-lose': brojBezSidrene > 0}">
        <span>Bez sidrene cijene</span>
        <b>{{ brojBezSidrene }}</b>
        <small>ne smiju u cjenik</small>
      </div>
    </div>

    <v-card outlined class="mb-3">
      <v-card-title class="sc-naslov-kartice">1. Napuni sidrene cijene iz cjenika</v-card-title>
      <v-card-text class="sc-tijelo">
        <div class="sc-upozorenje mb-3">
          Sidrena cijena je maloprodajna cijena s PDV-om koja je za artikl vrijedila na referentni datum,
          bez akcija i posebnih oblika prodaje. Punjenje iz današnjeg cjenika ispravno je samo ako se cijene
          od referentnog datuma nisu mijenjale — inače ih treba upisati ručno na artiklu.
        </div>
        <v-row no-gutters align="center">
          <v-col cols="12" sm="3" class="pr-2">
            <v-text-field label="Referentni datum" v-model="datumSidrenja" type="date" dense outlined hide-details class="mb-2"></v-text-field>
          </v-col>
          <v-col cols="12" sm="5" class="pr-2">
            <v-radio-group v-model="opsegPunjenja" row dense hide-details class="mt-0 mb-2">
              <v-radio label="samo prazne" value="prazne"></v-radio>
              <v-radio label="sve (prepiši i upisane)" value="sve"></v-radio>
            </v-radio-group>
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn small color="primary" :loading="punim" @click="napuni">Napuni iz cjenika</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="mb-3">
      <v-card-title class="sc-naslov-kartice">2. Podaci o objektu za naziv datoteke</v-card-title>
      <v-card-text class="sc-tijelo">
        <v-row no-gutters>
          <v-col cols="12" sm="3" class="pr-2">
            <v-select label="Vrsta objekta" :items="vrsteObjekta" v-model="vrstaObjekta" dense outlined hide-details class="mb-2"></v-select>
          </v-col>
          <v-col cols="12" sm="4" class="pr-2">
            <v-text-field label="Adresa objekta" v-model="adresaObjekta" dense outlined hide-details class="mb-2"></v-text-field>
          </v-col>
          <v-col cols="6" sm="2" class="pr-2">
            <v-text-field label="Oznaka objekta" v-model="oznakaObjekta" placeholder="P-01" dense outlined hide-details class="mb-2"></v-text-field>
          </v-col>
          <v-col cols="6" sm="3">
            <v-text-field label="Broj pohrane" v-model="brojPohrane" type="number" dense outlined hide-details class="mb-2"></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-checkbox v-model="sigurnoIme" dense hide-details class="mt-2"
              label="dvotočku u vremenu zamijeni crticom (za Windows i web poslužitelje)"></v-checkbox>
          </v-col>
          <v-col cols="12">
            <div class="sc-ime">{{ nazivDatoteke('csv') }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="mb-3">
      <v-card-title class="sc-naslov-kartice">3. Izvoz cjenika</v-card-title>
      <v-card-text class="sc-tijelo">
        <v-row no-gutters align="center">
          <v-col cols="12" sm="4" class="pr-2">
            <v-btn-toggle v-model="vrstaCjenika" mandatory dense class="mb-2">
              <v-btn small value="proizvodi">Proizvodi</v-btn>
              <v-btn small value="usluge">Usluge</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" sm="8">
            <v-btn small color="primary" class="mr-2 mb-2" :disabled="!zaCjenik.length" @click="izvozCsv">CSV</v-btn>
            <v-btn small outlined class="mr-2 mb-2" :disabled="!zaCjenik.length" @click="izvozXml">XML</v-btn>
            <v-btn small outlined class="mb-2" :disabled="!zaCjenik.length" @click="izvozExcel">Excel</v-btn>
          </v-col>
          <v-col cols="12">
            <div class="sc-sitno">
              U cjenik ide {{ zaCjenik.length }} {{ vrstaCjenika === 'usluge' ? 'usluga' : 'proizvoda' }}.
              <span v-if="izostavljeni > 0" class="sc-crveno">
                Izostavljeno {{ izostavljeni }} bez sidrene cijene.
              </span>
              CSV je razdvojen točka-zarezom, u UTF-8 s BOM-om, s decimalnim zarezom.
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div class="sc-alati">
      <span class="sc-oznaka">Traži</span>
      <input v-model="trazi" class="sc-trazi" type="search" placeholder="naziv, šifra ili barkod">
      <div class="sc-seg" role="group">
        <button type="button" :aria-pressed="String(filtar === 'svi')" @click="filtar = 'svi'">Svi</button>
        <button type="button" :aria-pressed="String(filtar === 'bez')" @click="filtar = 'bez'">Bez sidrene</button>
      </div>
      <span class="sc-broj">{{ prikaz.length }} od {{ artikli.length }}</span>
    </div>

    <v-progress-linear v-if="ucitavam" indeterminate color="blue" class="mb-2"></v-progress-linear>

    <div v-if="!ucitavam && !prikaz.length" class="sc-prazno">Nema artikala za prikaz.</div>
    <div v-else class="sc-scroll">
      <table class="sc-tbl">
        <thead>
          <tr>
            <th class="l">Naziv</th>
            <th class="l">Šifra</th>
            <th class="l">Barkod</th>
            <th>JM</th>
            <th>Cijena danas</th>
            <th>Sidrena cijena</th>
            <th>Na dan</th>
            <th>Razlika</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in prikaz" :key="a.id" :class="{'sc-red-bez': !imaSidrenu(a)}">
            <td class="l">
              {{ a.itemName }}
              <span v-if="a.itemService" class="sc-cip">usluga</span>
            </td>
            <td class="l">{{ a.itemID }}</td>
            <td class="l">{{ a.ItemEANCode }}</td>
            <td>{{ a.itemUnit }}</td>
            <td class="num">{{ novac(a.itemPriceWithVAT) }}</td>
            <td class="num">
              <span v-if="imaSidrenu(a)">{{ novac(a.sidrenaCijena) }}</span>
              <span v-else class="sc-crveno">nije utvrđena</span>
            </td>
            <td>{{ a.sidrenaCijenaDatum ? datum(a.sidrenaCijenaDatum) : '–' }}</td>
            <td class="num" :class="{'sc-crveno': razlika(a) > 0, 'sc-zeleno': razlika(a) < 0}">
              <span v-if="imaSidrenu(a)">{{ razlika(a) > 0 ? '+' : '' }}{{ novac(razlika(a)) }}</span>
              <span v-else>–</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </v-container>
</template>

<script>
// Sidrene (dodatne) cijene - Odluka o isticanju dodatne cijene i Odluka o
// objavi cjenika, NN 101/2026, u primjeni od 1. listopada 2026.
//
// Ekran radi tri stvari:
//   1. pokaze koji artikli jos nemaju utvrdjenu sidrenu cijenu,
//   2. napuni je iz vazeceg cjenika (jednim UPDATE-om na posluzitelju),
//   3. izveze cjenik u CSV, XML ili Excel prema propisanom sadrzaju.
//
// Artikl bez sidrene cijene NE IDE u cjenik. Radije je izostavljen nego
// objavljen s nulom ili s danasnjom cijenom - i jedno i drugo bi bila netocna
// izjava o cijeni na referentni datum.
import ItemService from '@/services/ItemService'
import dayjs from 'dayjs'
import xlsx from 'json-as-xlsx'

// Referentni datum sidrenja za sve proizvode i usluge. FMCG asortiman ima
// raniji (2.5.2025.) i za njega se datum mijenja rucno.
const REFERENTNI_DATUM = '2026-09-10'

export default {
  name: 'sidrenecijene',
  data () {
    return {
      artikli: [],
      comp: {},
      ucitavam: false,
      punim: false,
      snackbar: false,
      text: '',
      color: 'green',
      timeout: 4000,
      trazi: '',
      filtar: 'svi',
      datumSidrenja: REFERENTNI_DATUM,
      opsegPunjenja: 'prazne',
      vrstaCjenika: 'proizvodi',
      vrsteObjekta: ['prodavaonica', 'poslovnica', 'internetska trgovina', 'ugostiteljski objekt'],
      vrstaObjekta: 'prodavaonica',
      adresaObjekta: '',
      oznakaObjekta: '',
      brojPohrane: 1,
      sigurnoIme: true
    }
  },
  async mounted () {
    this.comp = this.$store.state.companyActiv ? this.$store.state.companyActiv : {}
    this.adresaObjekta = [
      [this.comp.CompanyStreet, this.comp.CompanyBuildingNameNumber].filter(x => x).join(' '),
      this.comp.CompanyCity
    ].filter(x => x).join(' ')
    this.oznakaObjekta = this.comp.CompanyBusinessSpace ? String(this.comp.CompanyBusinessSpace) : 'NP'
    await this.ucitaj()
  },
  computed: {
    brojProizvoda () {
      return this.artikli.filter(a => !a.itemService).length
    },
    brojUsluga () {
      return this.artikli.filter(a => a.itemService).length
    },
    brojSaSidrenom () {
      return this.artikli.filter(a => this.imaSidrenu(a)).length
    },
    brojBezSidrene () {
      return this.artikli.length - this.brojSaSidrenom
    },
    postotakSidrenih () {
      return this.artikli.length ? Math.round(this.brojSaSidrenom / this.artikli.length * 100) : 0
    },
    prikaz () {
      const t = this.trazi.trim().toLowerCase()
      return this.artikli.filter(a => {
        if (this.filtar === 'bez' && this.imaSidrenu(a)) { return false }
        if (!t) { return true }
        return String(a.itemName || '').toLowerCase().indexOf(t) !== -1 ||
          String(a.itemID || '').toLowerCase().indexOf(t) !== -1 ||
          String(a.ItemEANCode || '').indexOf(t) !== -1
      })
    },
    // Cjenik: samo trazena vrsta i samo artikli s utvrdjenom sidrenom cijenom.
    zaCjenik () {
      const usluge = this.vrstaCjenika === 'usluge'
      return this.artikli.filter(a => !!a.itemService === usluge && this.imaSidrenu(a))
    },
    izostavljeni () {
      const usluge = this.vrstaCjenika === 'usluge'
      return this.artikli.filter(a => !!a.itemService === usluge && !this.imaSidrenu(a)).length
    }
  },
  methods: {
    // NULL znaci "nije utvrdjena", 0 je stvarna cijena - zato se ne smije
    // provjeravati istinitost broja.
    imaSidrenu (a) {
      return a.sidrenaCijena !== null && a.sidrenaCijena !== undefined && a.sidrenaCijena !== ''
    },
    broj (v) {
      const n = parseFloat(v)
      return isNaN(n) ? 0 : n
    },
    novac (v) {
      return this.broj(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    datum (d) {
      return dayjs(d).format('DD.MM.YYYY')
    },
    razlika (a) {
      return this.broj(a.itemPriceWithVAT) - this.broj(a.sidrenaCijena)
    },
    javi (poruka, boja) {
      this.text = poruka
      this.color = boja || 'green'
      this.snackbar = true
    },
    async ucitaj () {
      this.ucitavam = true
      try {
        const {data} = await ItemService.sidrene({CompanyId: this.$store.state.companyid})
        this.artikli = data && data.items ? data.items : []
      } catch (err) {
        this.javi('Artikli nisu učitani: ' + (err && err.message ? err.message : err), 'red')
      }
      this.ucitavam = false
    },
    async napuni () {
      const sve = this.opsegPunjenja === 'sve'
      const pitanje = sve
        ? 'Prepisati sidrenu cijenu SVIM artiklima današnjom maloprodajnom cijenom? Već upisane vrijednosti bit će izgubljene.'
        : 'Upisati današnju maloprodajnu cijenu kao sidrenu za ' + this.brojBezSidrene + ' artikala bez nje?'
      if (!confirm(pitanje)) { return }
      this.punim = true
      try {
        const {data} = await ItemService.sidreneNapuni({
          CompanyId: this.$store.state.companyid,
          email: this.$store.state.user ? this.$store.state.user.email : null,
          datum: this.datumSidrenja,
          svi: sve
        })
        this.javi('Postavljeno na ' + (data ? data.promijenjeno : 0) + ' artikala.')
        await this.ucitaj()
      } catch (err) {
        this.javi('Punjenje nije uspjelo: ' + (err && err.message ? err.message : err), 'red')
      }
      this.punim = false
    },

    // --- naziv datoteke -----------------------------------------------------
    // Propisani oblik: vrsta objekta_adresa i oznaka objekta_broj pohrane_datum
    // i vrijeme slanja. Dvotocka iz primjera ("07:45") nije dopustena u nazivu
    // datoteke na Windowsu, pa se po izboru zamjenjuje crticom.
    nazivDatoteke (nastavak) {
      const sad = dayjs()
      const vrijeme = sad.format(this.sigurnoIme ? 'HH-mm' : 'HH:mm')
      const dijelovi = [
        this.vrstaObjekta || 'prodavaonica',
        [this.adresaObjekta, this.oznakaObjekta].filter(x => x).join('_'),
        String(this.brojPohrane || 1),
        sad.format('DD.MM.YYYY') + '_' + vrijeme
      ]
      return dijelovi.join('_') + '.' + nastavak
    },
    spremi (sadrzaj, vrsta, nastavak) {
      const blob = new Blob([sadrzaj], {type: vrsta})
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = this.nazivDatoteke(nastavak)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      this.brojPohrane = parseInt(this.brojPohrane, 10) + 1
    },

    // --- sadrzaj cjenika ----------------------------------------------------
    // Za proizvode i za usluge propisan je razlicit skup podataka, pa se redci
    // grade posebno. Polja koja sifrarnik nema (marka, neto kolicina) ostaju
    // prazna umjesto da se izmisljaju.
    stupci () {
      if (this.vrstaCjenika === 'usluge') {
        return ['Naziv usluge', 'Maloprodajna cijena', 'Posebni oblik prodaje',
          'Naziv posebnog oblika prodaje', 'Sidrena cijena', 'Datum sidrenja']
      }
      return ['Naziv proizvoda', 'Šifra proizvoda', 'Marka', 'Neto količina', 'Jedinica mjere',
        'Cijena za jedinicu mjere', 'Maloprodajna cijena', 'Posebni oblik prodaje',
        'Naziv posebnog oblika prodaje', 'Sidrena cijena', 'Datum sidrenja', 'Barkod', 'Dostupnost']
    },
    redak (a) {
      const cijena = this.broj(a.itemPriceWithVAT)
      const sidrena = this.broj(a.sidrenaCijena)
      const datumS = a.sidrenaCijenaDatum ? this.datum(a.sidrenaCijenaDatum) : this.datum(REFERENTNI_DATUM)
      if (this.vrstaCjenika === 'usluge') {
        return [a.itemName || '', cijena, 'NE', '', sidrena, datumS]
      }
      return [
        a.itemName || '',
        a.itemID || '',
        '',
        '',
        a.itemUnit || '',
        cijena,
        cijena,
        'NE',
        '',
        sidrena,
        datumS,
        a.ItemEANCode || '',
        'dostupno'
      ]
    },
    // Decimalni zarez i tocka-zarez kao razdjelnik - tako se datoteka otvara i
    // u hrvatskom Excelu. BOM jer bez njega Excel procita ČĆŽŠĐ kao smece.
    izvozCsv () {
      const ucsv = (v) => {
        if (typeof v === 'number') { return v.toFixed(2).replace('.', ',') }
        const s = String(v === null || v === undefined ? '' : v)
        return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
      }
      const redci = [this.stupci().join(';')]
      this.zaCjenik.forEach(a => { redci.push(this.redak(a).map(ucsv).join(';')) })
      this.spremi('﻿' + redci.join('\r\n') + '\r\n', 'text/csv;charset=utf-8', 'csv')
    },
    izvozXml () {
      const esc = (v) => String(v === null || v === undefined ? '' : v)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
      // Nazivi elemenata bez razmaka i dijakritike - XML ime ne smije imati
      // razmak, a citaci ne vole nasa slova u nazivima.
      const ime = (s) => s
        .replace(/[čć]/g, 'c').replace(/ž/g, 'z').replace(/š/g, 's').replace(/đ/g, 'd')
        .replace(/[ČĆ]/g, 'C').replace(/Ž/g, 'Z').replace(/Š/g, 'S').replace(/Đ/g, 'D')
        .replace(/\s+/g, '_')
      const stupci = this.stupci().map(ime)
      const korijen = this.vrstaCjenika === 'usluge' ? 'CjenikUsluga' : 'CjenikProizvoda'
      const stavka = this.vrstaCjenika === 'usluge' ? 'Usluga' : 'Proizvod'
      const redci = this.zaCjenik.map(a => {
        const v = this.redak(a)
        const polja = stupci.map((s, i) => {
          const vrijednost = typeof v[i] === 'number' ? v[i].toFixed(2) : v[i]
          return '    <' + s + '>' + esc(vrijednost) + '</' + s + '>'
        })
        return '  <' + stavka + '>\n' + polja.join('\n') + '\n  </' + stavka + '>'
      })
      const glava = '<?xml version="1.0" encoding="UTF-8"?>\n<' + korijen +
        ' naziv="' + esc(this.comp.CompanyNameShort) + '"' +
        ' oib="' + esc(this.comp.CompanyVATID) + '"' +
        ' objekt="' + esc(this.vrstaObjekta + ' ' + this.adresaObjekta + ' ' + this.oznakaObjekta) + '"' +
        ' datum="' + dayjs().format('DD.MM.YYYY') + '"' +
        ' vrijeme="' + dayjs().format('HH:mm') + '">\n'
      this.spremi(glava + redci.join('\n') + '\n</' + korijen + '>\n', 'application/xml;charset=utf-8', 'xml')
    },
    izvozExcel () {
      const stupci = this.stupci().map((s, i) => ({label: s, value: (red) => red[i]}))
      const sadrzaj = this.zaCjenik.map(a => this.redak(a))
      xlsx(stupci, sadrzaj, {
        sheetName: this.vrstaCjenika === 'usluge' ? 'Cjenik usluga' : 'Cjenik proizvoda',
        fileName: this.nazivDatoteke('').replace(/\.$/, '')
      })
      this.brojPohrane = parseInt(this.brojPohrane, 10) + 1
    }
  }
}
</script>

<style scoped>
.sc { padding: 8px 12px; }
.sc-head {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 12px;
  align-items: start;
  padding-bottom: 10px;
  border-bottom: 2px solid #1976d2;
}
.sc-firma { font-size: 13px; color: #4a524f; line-height: 1.4; }
.sc-firma-ime { font-weight: 700; color: #1a1f1d; font-size: 15px; }
.sc-naslov { text-align: right; }
.sc-naslov h2 { font-size: 20px; font-weight: 700; color: #1565c0; margin: 0; }
.sc-meta { font-size: 12px; color: #4a524f; }
.sc-sep { margin: 0 6px; }
.sc-akcije { display: flex; gap: 8px; }

.sc-sazetak {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  margin: 14px 0;
}
.sc-kut {
  border: 1px solid #e0e4e2;
  border-radius: 6px;
  padding: 8px 12px;
  background: #fafbfc;
}
.sc-kut span { display: block; font-size: 12px; color: #4a524f; }
.sc-kut b { display: block; font-size: 20px; font-variant-numeric: tabular-nums; }
.sc-kut small { display: block; font-size: 11px; color: #6b7370; }
.sc-istaknuto { background: #eef4fb; border-color: #bcd3ea; }
.sc-lose { background: #fdecea; border-color: #f3c0ba; }

.sc-naslov-kartice {
  font-size: 15px;
  font-weight: 700;
  color: #1565c0;
  background: #eef4fb;
  border-bottom: 2px solid #1976d2;
  padding: 8px 16px;
  min-height: 0;
}
/* Obrubljena polja imaju plutajucu oznaku koja sjedi NA obrubu, pa je s
   uobicajenim razmakom kartice naslijepo naslonjena na plavi naslov. Odatle
   veci razmak odozgo nego sa strana. */
.sc-tijelo {
  padding-top: 22px;
}
.sc-upozorenje {
  background: #fff8e1;
  border-left: 3px solid #f9a825;
  padding: 8px 12px;
  font-size: 13px;
  color: #4a3f1f;
}
.sc-ime {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: #37474f;
  background: #f4f6f8;
  border-radius: 4px;
  padding: 6px 10px;
  margin-top: 6px;
  overflow-wrap: anywhere;
}
.sc-sitno { font-size: 12px; color: #4a524f; margin-top: 6px; }
.sc-crveno { color: #c62828; }
.sc-zeleno { color: #2e7d32; }

.sc-alati { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 12px 0 6px; }
.sc-oznaka { font-size: 12px; color: #4a524f; }
.sc-trazi {
  border: 1px solid #c8d3de;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 13px;
  min-width: 220px;
}
.sc-seg button {
  border: 1px solid #c8d3de;
  padding: 4px 12px;
  font-size: 12px;
  background: #fff;
}
.sc-seg button[aria-pressed="true"] { background: #1976d2; color: #fff; border-color: #1976d2; }
.sc-broj { font-size: 12px; color: #4a524f; margin-left: auto; }
.sc-prazno { padding: 28px 4px; color: #4a524f; }

.sc-scroll { overflow-x: auto; }
.sc-tbl { width: 100%; border-collapse: collapse; font-size: 13px; }
.sc-tbl th, .sc-tbl td { padding: 6px 10px; border-bottom: 1px solid #eceff1; text-align: right; white-space: nowrap; }
.sc-tbl th.l, .sc-tbl td.l { text-align: left; }
.sc-tbl th { font-size: 12px; color: #4a524f; background: #f7f9fa; position: sticky; top: 0; }
.sc-tbl td.num { font-variant-numeric: tabular-nums; }
.sc-tbl .sc-red-bez { background: #fff6f5; }
.sc-cip {
  display: inline-block;
  margin-left: 6px;
  border-radius: 9px;
  padding: 0 7px;
  font-size: 11px;
  background: #e8f5e9;
  color: #2e7d32;
}

@media (max-width: 900px) {
  .sc-head { grid-template-columns: 1fr; }
  .sc-naslov { text-align: left; }
}
</style>
